import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { get } from "@/lib/api";
import { EmptyState, ErrorState, FreshnessBadge, KpiTile, Loading, Panel, RunStatusBadge, StateBadge, Timestamp } from "@/components/common";
import { Sparkline } from "@/components/charts";
import { FeedNote } from "@/components/FeedWatcher";
import { POLLUTANT_LABEL, ROLE_LABEL } from "@/lib/format";

export default function Dashboard() {
  const { data, isLoading, error } = useQuery({ queryKey: ["dashboard"], queryFn: () => get("/system/dashboard") });
  if (isLoading) return <Loading />;
  if (error) return <ErrorState message="Dashboard could not be loaded" />;
  const { kpis, stations, recent_events, trends, ingestion } = data;
  const open = (kpis.events_by_state.DETECTED || 0) + (kpis.events_by_state.TRIAGED || 0) + (kpis.events_by_state.UNDER_REVIEW || 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">Operations dashboard</h1>
          <p className="mt-1 text-sm text-slate-500">Tin Can Island demonstrator · synthetic day 2026-10-18 · all times UTC with WAT alongside</p>
        </div>
        <Link to="/events" data-testid="dashboard-events-link" className="text-sm text-lime-300 hover:underline">Open event queue →</Link>
      </div>
      <FeedNote />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <KpiTile label="Stations OK" value={`${kpis.stations_ok}/${kpis.stations_total}`} sub="freshness ≤ 15 min" accent="cyan" testId="kpi-stations" />
        <KpiTile label="Vessels tracked" value={`${kpis.vessels_tracked}/${kpis.vessels_total}`} sub="AIS fix ≤ 10 min" accent="slate" testId="kpi-vessels" />
        <KpiTile label="Open events" value={open} sub="detected · triaged · review" accent="amber" testId="kpi-open-events" />
        <KpiTile label="Events total" value={kpis.events_total} sub="synthetic day" accent="slate" testId="kpi-events-total" />
        <KpiTile label="Decisions" value={kpis.decisions_total} sub={`${kpis.verified_total} verified by humans`} accent="emerald" testId="kpi-decisions" />
        <KpiTile label="Inconclusive" value={kpis.events_by_state.INCONCLUSIVE || 0} sub="a first-class outcome" accent="violet" testId="kpi-inconclusive" />
      </div>

      <div className="grid gap-4 xl:grid-cols-[1.4fr_1fr]">
        <Panel title="Recent events" subtitle="Detection never names a vessel; attribution is a separate action" testId="recent-events-panel">
          {recent_events.length === 0 ? <EmptyState title="No events detected" /> : (
            <table className="w-full text-sm">
              <thead className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="pb-2">Event</th><th className="pb-2">Pollutant</th><th className="pb-2">First observed</th><th className="pb-2">Stations</th><th className="pb-2">State</th><th className="pb-2">Attribution</th></tr></thead>
              <tbody className="divide-y divide-[#1E3E30]">
                {recent_events.map((e) => (
                  <tr key={e.event_id} data-testid={`dashboard-event-row-${e.event_id}`} className="hover:bg-white/[0.02]">
                    <td className="py-2"><Link to={`/events/${e.event_id}`} className="font-mono text-xs text-lime-300 hover:underline">{e.event_id}</Link>{e.scenario_code && <span className="ml-2 font-mono text-[10px] text-slate-600">{e.scenario_code}</span>}</td>
                    <td className="py-2">{POLLUTANT_LABEL[e.main_pollutant]}</td>
                    <td className="py-2"><Timestamp ts={e.first_observed_at} withDate={false} /></td>
                    <td className="py-2 font-mono text-xs text-slate-400">{e.station_ids.join(", ")}</td>
                    <td className="py-2"><StateBadge state={e.state} /></td>
                    <td className="py-2">{e.latest_attribution_status ? <RunStatusBadge status={e.latest_attribution_status} /> : <span className="text-xs text-slate-600">not run</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
        <Panel title="Ingestion health" subtitle="Provider status · no new data ≠ no pollution" testId="ingestion-panel">
          <ul className="space-y-2">
            {ingestion.map((j) => (
              <li key={j.job_id} data-testid={`ingestion-${j.provider}`} className="flex items-center justify-between rounded border border-[#1E3E30] px-3 py-2">
                <div><div className="font-mono text-xs text-slate-200">{j.provider}</div><div className="text-[11px] text-slate-500">{j.kind.replace("_", " ").toLowerCase()} · last run <Timestamp ts={j.last_run_at} withDate={false} /></div></div>
                <span className={`font-mono text-[11px] ${j.freshness?.status === "OK" ? "text-emerald-300" : "text-amber-300"}`}>{j.freshness?.status}{j.retry_queue ? ` · ${j.retry_queue} retry` : ""}</span>
              </li>
            ))}
          </ul>
          <Link to="/admin?tab=health" className="mt-3 block text-xs text-lime-300 hover:underline">Full ingestion health →</Link>
        </Panel>
      </div>

      <Panel title="Station network · 24 h trend" subtitle="Primary pollutant sparkline per station; gaps are drawn, not smoothed" testId="station-trends-panel">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {stations.map((s) => (
            <Link key={s.station_id} to={`/sensors/${s.station_id}`} data-testid={`station-tile-${s.station_id}`} className="rounded border border-[#1E3E30] bg-[#11241C] p-3 transition-colors hover:border-lime-500/40">
              <div className="flex items-start justify-between gap-2">
                <div><div className="text-sm font-semibold text-slate-100">{s.name}</div><div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{s.code} · {ROLE_LABEL[s.role]}</div></div>
                <FreshnessBadge freshness={s.freshness} />
              </div>
              {s.role === "MET" ? (
                <div className="mt-3 font-mono text-xs text-slate-400">wind FROM {s.latest?.wind?.wind_from_deg}° · {s.latest?.wind?.wind_speed_mps} m/s</div>
              ) : (
                <>
                  <div className="mt-2"><Sparkline points={trends[s.station_id]?.points} /></div>
                  <div className="mt-1 flex justify-between font-mono text-[10px] text-slate-500"><span>{POLLUTANT_LABEL[trends[s.station_id]?.pollutant]}</span><span>last obs <Timestamp ts={s.freshness.last_observed_at} withDate={false} className="text-[10px]" /></span></div>
                </>
              )}
            </Link>
          ))}
        </div>
      </Panel>
    </div>
  );
}
